import React, { useState, useEffect } from 'react';
import {
	Box,
	Typography,
	Dialog,
	DialogContent,
	Stepper,
	Step,
	StepLabel,
	StepContent,
	TextField,
	MenuItem,
	Button,
	InputAdornment,
	Chip,
	Collapse,
	CircularProgress,
	ListItem,
	ListItemText,
	ListItemIcon,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { ApiDocsTagUrl } from 'common/constants/api';

import { useTranslation } from 'services/localization';

import JSONBlock from 'components/JSONBlock';
import ExternalLinkButton from 'common/components/ExternalLinkButton';
import { sleep } from 'common/utils/async';
import { pingWebhook, getWebhookTestPayload } from 'common/api/db/functions';
import { WebhookInfo, WebhookEvent } from 'common/types/api';
import {
	validateWebhookURL,
	validateWebhook,
	translateWebhookValidationError,
	translateWebhookEvent,
	WebhookInvalidReason,
	getPayloadTypeForWebhookEvent,
} from 'common/utils/webhooks';
import { api } from 'common/frontend/api';
import { newFirestoreId } from 'common/utils/newRentalUtils';
import Banner from 'components/Banner';

interface Props {
	isOpen: boolean;
	onClose: () => void;
	shopId: string;
	existingLabels?: string[];
}

const CreateWebhookModal = (props: Props) => {
	const { t } = useTranslation();
	const [activeStep, setActiveStep] = useState<number>(0);
	const [URLValue, setURLValue] = useState<string>('');
	const [URLError, setURLError] = useState<WebhookInvalidReason | null>(null);
	const [testPayload, setTestPayload] = useState<object>({});
	const [formState, setFormState] = useState<Partial<WebhookInfo>>({});
	const [formError, setFormError] = useState<string | null>(null);
	const [pingState, setPingState] = useState<'initial' | 'loading' | 'error' | 'done'>('initial');
	const [pingError, setPingError] = useState<string | null>(null);
	const [submitState, setSubmitState] = useState<'initial' | 'loading' | 'done'>('initial');

	const payloadType = formState.event ? getPayloadTypeForWebhookEvent(formState.event) : null;

	const isDuplicateLabel =
		(!!formState.label && props.existingLabels?.includes(formState.label)) ?? false;

	useEffect(() => {
		if (formState.event) {
			getWebhookTestPayload({ event: formState.event })
				.then((payload) => {
					setTestPayload(payload);
				})
				.catch((e) => {});
		} else {
			setTestPayload({});
		}
	}, [formState.event]);

	const handleEventChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setFormState((prev) => ({
			...prev,
			event: e.target.value as WebhookEvent,
		}));
	};

	const handleLabelChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setFormState((prev) => ({
			...prev,
			label: e.target.value,
		}));
	};

	const handleURLChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const value = e.target.value;
		if (URLError) {
			setURLError(validateWebhookURL(value, { blacklist: formState.urls }));
		}
		setURLValue(value);
	};

	const handleAddURL = () => {
		const validationError = validateWebhookURL(URLValue, { blacklist: formState.urls });
		if (validationError) {
			setURLError(validationError);
		} else {
			setFormState((prev) => ({
				...prev,
				urls: [...(prev.urls ?? []), URLValue],
			}));
			setPingState('initial');
		}
	};

	const handleRemoveURL = (url: string) => {
		setFormState((prev) => ({
			...prev,
			urls: prev.urls?.filter((u) => u !== url) ?? [],
		}));
	};

	const handleReset = () => {
		setFormState({});
		setSubmitState('initial');
		setPingState('initial');
		setURLValue('');
		setURLError(null);
		setActiveStep(0);
	};

	const handlePing = async () => {
		setPingState('loading');
		setPingError(null);
		await sleep(1000);
		try {
			const webhookError = await pingWebhook({ webhook: formState });
			if (webhookError) {
				setPingError(webhookError);
				setPingState('error');
			} else {
				setPingState('done');
			}
		} catch (e) {
			setPingError(
				t(
					'Integrations.Webhooks.CreateWebhookModal.unknownError',
					'Unknown error - please try again later.',
				),
			);
			setPingState('error');
		}
	};

	const handleSubmit = async () => {
		setSubmitState('loading');
		const validation = validateWebhook(formState, {
			blacklistedLabels: [], //TODO: Existing webhook labels here,
		});
		if (!validation.isValid) {
			setFormError(translateWebhookValidationError(validation.reason, formState, t));
		} else {
			setSubmitState('loading');
			const docId = newFirestoreId();
			const webhook: WebhookInfo = {
				...validation.data,
				id: docId,
			};
			try {
				await api().shops.doc(props.shopId).webhooks.doc(webhook.id).set(webhook);
				setSubmitState('done');
			} catch (e) {
				setFormError(
					t(
						'Integrations.Webhooks.CreateWebhookModal.unknownError',
						'Unknown error - please try again later.',
					),
				);
				setSubmitState('initial');
			}
		}
	};

	const renderStepButtons = (
		currentStep: number,
		options: { nextDisabled: boolean; nextLabel?: string; isLastStep?: boolean },
	) => {
		return (
			<Box display="flex" flexDirection="row">
				{currentStep !== 0 && (
					<Box mr={2}>
						<Button
							color="secondary"
							variant="outlined"
							onClick={() => setActiveStep(currentStep - 1)}
						>
							{t('common:actions.back')}
						</Button>
					</Box>
				)}
				{!options.isLastStep ? (
					<Button
						variant="contained"
						color="primary"
						disabled={options.nextDisabled}
						onClick={() => setActiveStep(currentStep + 1)}
					>
						{options.nextLabel ?? t('common:actions.next')}
					</Button>
				) : (
					<Button
						variant="contained"
						color="primary"
						disabled={options.nextDisabled}
						onClick={handleSubmit}
					>
						{t('Integrations.Webhooks.CreateWebhookModal.createWebhook', 'Create webhook')}
					</Button>
				)}
			</Box>
		);
	};

	return (
		<Dialog
			open={props.isOpen}
			onClose={props.onClose}
			aria-labelledby="webhook-create-modal-title"
			fullWidth
			maxWidth="md"
			disableEscapeKeyDown={submitState === 'loading'}
			TransitionProps={{
				onExited: handleReset,
			}}
		>
			<DialogContent>
				<Collapse in={submitState === 'initial'}>
					<Typography variant="h5" id="webhook-create-modal-title" gutterBottom>
						{t('Integrations.Webhooks.CreateWebhookModal.createWebhook', 'Create webhook')}
					</Typography>
					{formError && <Banner title={formError} variant="danger" />}
					<Stepper activeStep={activeStep} orientation="vertical">
						<Step>
							<StepLabel>
								{t('Integrations.Webhooks.CreateWebhookModal.triggersOn', {
									event: formState.event ?? '',
									defaultValue: 'Triggers on {{event}}',
								})}
							</StepLabel>
							<StepContent>
								<Typography variant="body2" color="textSecondary">
									{t(
										'Integrations.Webhooks.CreateWebhookModal.triggersOnDescription',
										'Which event do you want this webhook to trigger on?',
									)}
								</Typography>
								<Box my={3} maxWidth={300}>
									<TextField
										select
										label={t('common:actions.chooseOne', 'Choose one')}
										value={formState.event ?? ''}
										onChange={handleEventChange}
										variant="outlined"
										fullWidth
									>
										{Object.keys(WebhookEvent).map((event) => (
											<MenuItem key={event} value={event}>
												{translateWebhookEvent(WebhookEvent[event], t)}
											</MenuItem>
										))}
									</TextField>
								</Box>
								{renderStepButtons(0, { nextDisabled: !formState.event })}
							</StepContent>
						</Step>
						<Step>
							<StepLabel>
								{activeStep > 1 && formState.urls ? `URL: ${formState.urls.length}` : 'URL'}
							</StepLabel>
							<StepContent>
								<Typography variant="body2" color="textSecondary">
									{t(
										'Integrations.Webhooks.CreateWebhookModal.urlsDescription',
										'Enter up to five URLs that should be called when the selected event occurs',
									)}
								</Typography>
								<Box my={3}>
									<Box display="flex" flexDirection="row" alignItems="center">
										<TextField
											label={t('Integrations.Webhooks.CreateWebhookModal.enterUrl', 'Enter URL')}
											variant="outlined"
											fullWidth
											value={URLValue}
											onChange={handleURLChange}
											helperText={
												URLError
													? translateWebhookValidationError(URLError, formState, t)
													: t(
															'Integrations.Webhooks.CreateWebhookModal.enterUrlHelper',
															'If you need a test URL to play around with, you can get one here: https://webhook.site',
													  )
											}
											error={!!URLError}
											InputProps={{
												endAdornment: (
													<InputAdornment position="end">
														<Button
															disabled={
																!!URLError || (formState.urls && formState.urls.length >= 5)
															}
															variant="outlined"
															color="primary"
															onClick={handleAddURL}
														>
															{t('common:actions.add')}
														</Button>
													</InputAdornment>
												),
											}}
										/>
									</Box>
									{formState?.urls?.map((url) => (
										<Box key={url} mt={1}>
											<Chip label={url} onDelete={() => handleRemoveURL(url)} />
										</Box>
									))}
								</Box>
								{renderStepButtons(1, {
									nextDisabled: !formState.urls || formState.urls.length === 0,
								})}
							</StepContent>
						</Step>
						<Step>
							<StepLabel>
								{t('Integrations.Webhooks.CreateWebhookModal.testWebhook', 'Test your webhook')}
							</StepLabel>
							<StepContent>
								<Typography variant="body2">
									{t(
										'Integrations.Webhooks.CreateWebhookModal.testWebhookDescription',
										`Click the button below to send a test request to your webhook to verify that it
									works like intended. The webhook will send a POST request to your provided URLs
									with the following test payload:`,
									)}
								</Typography>
								<Box my={3}>
									<Box style={{ position: 'relative' }}>
										<JSONBlock json={testPayload} maxHeight={400} />
										<Box style={{ position: 'absolute', bottom: 8, right: 8 }}>
											<ExternalLinkButton
												href={ApiDocsTagUrl(payloadType ?? '')}
												text={t('Integrations.Webhooks.CreateWebhookModal.docsForPayload', {
													payloadType,
													defaultValue: 'API Docs for payload ({{payloadType}})',
												})}
											/>
										</Box>
									</Box>
									<Box mt={1}>
										<Button
											disabled={pingState === 'loading'}
											color="primary"
											variant={pingState === 'done' ? 'outlined' : 'contained'}
											onClick={handlePing}
										>
											{pingState === 'error'
												? t('common:actions.retry')
												: t(
														'Integrations.Webhooks.CreateWebhookModal.sendTestRequest',
														'Send test request',
												  )}
										</Button>

										{pingState === 'loading' && (
											<ListItem>
												<ListItemIcon>
													<CircularProgress size={20} />
												</ListItemIcon>
												<ListItemText
													primary={t(
														'Integrations.Webhooks.CreateWebhookModal.sendingTestRequest',
														'Sending test request...',
													)}
												/>
											</ListItem>
										)}
										{pingState === 'error' && (
											<>
												<ListItem>
													<ListItemIcon>
														<ErrorOutlineIcon />
													</ListItemIcon>
													<ListItemText
														primary={t(
															'Integrations.Webhooks.CreateWebhookModal.testFailed',
															'Test failed',
														)}
														secondary={t(
															'Integrations.Webhooks.CreateWebhookModal.testFailedDescription',
															{
																errorMessage: pingError,
																defaultValue:
																	'The webhook test failed with the following error message: {{errorMessage}}',
															},
														)}
													/>
												</ListItem>
											</>
										)}
										{pingState === 'done' && (
											<ListItem>
												<ListItemIcon>
													<CheckIcon color="primary" />
												</ListItemIcon>
												<ListItemText
													primary={t('common:states.success', 'Success')}
													secondary={t(
														'Integrations.Webhooks.CreateWebhookModal.pingSuccessDescription',
														`The webhook was able to successfully send data to the URLs you provided. However, this only means that the data was sent - before continuing you should check the services receiving this webhook and make sure everything is working correctly.`,
													)}
												/>
											</ListItem>
										)}
									</Box>
								</Box>
								{renderStepButtons(2, {
									nextDisabled: pingState === 'loading',
									nextLabel: pingState !== 'done' ? t('common:actions.skip') : undefined,
								})}
							</StepContent>
						</Step>
						<Step>
							<StepLabel>{t('Integrations.Webhooks.CreateWebhookModal.label', 'Label')}</StepLabel>
							<StepContent>
								<Typography variant="body2" color="textSecondary">
									{t(
										'Integrations.Webhooks.CreateWebhookModal.labelDescription',
										'Give your webhook a descriptive label',
									)}
								</Typography>
								<Box my={3}>
									<TextField
										label={t('Integrations.Webhooks.CreateWebhookModal.labelTitle', 'Enter label')}
										variant="outlined"
										fullWidth
										value={formState.label ?? ''}
										onChange={handleLabelChange}
										helperText={
											isDuplicateLabel
												? t(
														'Integrations.Webhooks.CreateWebhookModal.duplicateLabel',
														'You already have a webhook with that label',
												  )
												: null
										}
										error={isDuplicateLabel}
									/>
								</Box>
								{renderStepButtons(3, {
									nextDisabled: !formState.label || isDuplicateLabel,
									isLastStep: true,
								})}
							</StepContent>
						</Step>
					</Stepper>
				</Collapse>
				<Collapse in={submitState === 'loading'}>
					<Box
						p={3}
						minHeight={300}
						display="flex"
						flexDirection="column"
						alignItems="center"
						justifyContent="center"
					>
						<CircularProgress size={20} />
						<Box mt={2} />
						<Typography variant="h5" gutterBottom>
							{t(
								'Integrations.Webhooks.CreateWebhookModal.creatingWebhook',
								'Creating your webhook...',
							)}
						</Typography>
					</Box>
				</Collapse>
				<Collapse in={submitState === 'done'}>
					<Box
						p={3}
						minHeight={300}
						display="flex"
						flexDirection="column"
						alignItems="center"
						justifyContent="center"
					>
						<Typography variant="h5" gutterBottom>
							{t('Integrations.Webhooks.CreateWebhookModal.webhookCreated', 'Webhook created!')}
						</Typography>
						<Box mt={2} />
						<Button color="primary" variant="contained" onClick={props.onClose}>
							{t('common:actions.done')}
						</Button>
					</Box>
				</Collapse>
			</DialogContent>
		</Dialog>
	);
};

export default CreateWebhookModal;
